import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../../services/profile.service';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { usaPhoneValidator } from '../../shared/validators/phone.validator';
import { strictEmailValidator } from '../../shared/validators/email.validator';
import {
  CONTACT_UPDATE_FIELDS,
  buildLocationUpdatePayload,
} from '../../shared/utils/location-details.util';

/**
 * Component for viewing and editing a location's contact details (email and phone).
 * @component
 * @selector app-contact-details
 * @example
 *   <app-contact-details
 *     [locationDetails]="location"
 *     [startEditable]="true"
 *     (cancelClicked)="onCancel()"
 *     (formInteracted)="onFormInteracted()"
 *     (locationUpdated)="onLocationUpdated($event)">
 *   </app-contact-details>
 *
 * Purpose:
 *   Displays contact information for a location and provides an editable reactive form to update
 *   email and phone. Handles form validation, submission, refetching fresh data, and notifies
 *   the parent component about interactions and updates.
 *
 * Inputs:
 * @input {any} locationDetails - Initial location data (expects at least `email`, `contact`, and `id`). When changed, the form is patched and original values are stored for potential cancel/restore.
 * @input {boolean} startEditable - If true, the component enters edit mode on initialization.
 *
 * Outputs:
 * @output {EventEmitter<void>} cancelClicked - Emitted when editing is cancelled or after a successful save to signal the parent to close/reset UI.
 * @output {EventEmitter<void>} formInteracted - Emitted on any form value change to notify parent that the user interacted with the form.
 * @output {EventEmitter<any>} locationUpdated - Emitted with the updated location data after a successful save and refetch from the server.
 *
 * Key public properties:
 *   - contactForm: FormGroup - Reactive form containing `email` and `contact` controls with validators.
 *   - isEditing: boolean - Whether the form is in edit mode.
 *   - isFormModified: boolean - Tracks whether the current form values differ from the original values.
 *   - isSubmitting: boolean - Indicates an ongoing submit operation.
 *
 * Key methods:
 *   - onSubmit(): Validates and submits the form. Calls profileService.updateLocationDetails, refetches the saved location details,
 *     updates originalFormData and the form with fresh values, emits locationUpdated and cancelClicked on success, and shows user messages.
 *   - onEdit(): Enables editing, stores a copy of the current form values in originalFormData, resets modification tracking, and enables the form controls.
 *   - onCancel(): Restores the form to originalFormData, disables edit mode, resets modification tracking, and emits cancelClicked.
 *   - get email(), get phone(): Convenience getters for form controls.
 *   - private checkIfFormModified(): Compares current form values with originalFormData and sets isFormModified accordingly.
 *
 * Lifecycle hooks:
 *   - ngOnChanges(changes: SimpleChanges): Patches the form and records originalFormData whenever locationDetails input changes.
 *   - ngOnInit(): Optionally enters edit mode if startEditable is true and subscribes to form valueChanges to emit formInteracted and to check modification state.
 */
@Component({
  selector: 'app-contact-details',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule],
  templateUrl: './contact-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contact-details.component.scss'],
})
export class ContactDetailsComponent implements OnChanges, OnInit, OnDestroy {
  private readonly destroy = new Subject<void>();
  @Input() locationDetails: any;
  public contactForm: FormGroup;
  isEditing = false;
  isFormModified = false;
  isSubmitting = false;
  @Output() cancelClicked = new EventEmitter<void>();
  @Output() formInteracted = new EventEmitter<void>();
  @Output() locationUpdated = new EventEmitter<any>();
  @Input() startEditable = false;

  // Store original form values for cancellation
  private originalFormData: any = {};

  /**
   * Constructs the ContactDetailsComponent and initializes the reactive contactForm with email and contact controls and their validators.
   * @example
   * new ContactDetailsComponent(fb, profileService, messageService)
   * // returns an initialized ContactDetailsComponent instance with contactForm configured
   * @param {FormBuilder} fb - Angular FormBuilder used to create the form group.
   * @param {ProfileService} profileService - Service providing profile-related operations (injected).
   * @param {NotificationService} messageService - Service used to display messages or notifications (injected).
   * @returns {void} Constructs the component and initializes the contactForm FormGroup.
   **/
  constructor(
    private fb: FormBuilder,
    private profileService: ProfileService,
    private messageService: NotificationService
  ) {
    this.contactForm = this.fb.group({
      email: [
        { value: '' },
        [Validators.required, Validators.maxLength(254), strictEmailValidator()],
      ],
      contact: [
        { value: '' },
        [Validators.required, Validators.maxLength(15), usaPhoneValidator()],
      ],
    });
  }

  /**
   * Update the contact form when the input property `locationDetails` changes.
   * @example
   * gOnChanges({ locationDetails: { currentValue: { email: 'user@example.com', contact: '555-1234' } } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - SimpleChanges object containing input-bound property changes; used to detect and apply `locationDetails` to the contact form.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['locationDetails'] && this.locationDetails) {
      const formData = {
        email: this.locationDetails.email,
        contact: this.locationDetails.contact,
      };

      this.contactForm.patchValue(formData);
      // Store the original data for potential restoration
      this.originalFormData = { ...formData };
    }
  }

  ngOnInit(): void {
    if (this.startEditable) {
      this.onEdit();
    }
    this.contactForm.valueChanges.pipe(takeUntil(this.destroy)).subscribe(() => {
      this.formInteracted.emit();

      this.checkIfFormModified();
    });
  }
  get email() {
    return this.contactForm.get('email');
  }

  get phone() {
    return this.contactForm.get('contact');
  }

  /**
   * Submits the contact form: validates inputs, builds and sends an update payload to the profile service, refetches fresh location details on success, updates local form data/state, emits events, and displays success/error messages.
   * @example
   * nSubmit()
   * // void
   * @returns {void} No return value; performs side effects on the component (HTTP calls, form/state updates, emits events and shows messages).
   */
  onSubmit() {
    if (this.contactForm.valid) {
      this.isSubmitting = true;
      const payload = buildLocationUpdatePayload(this.contactForm.value, {
        allowedFields: CONTACT_UPDATE_FIELDS,
      });

      this.profileService.updateLocationDetails(this.locationDetails.id, payload).subscribe({
        next: () => {
          // Refetch location details from API to get the latest data
          this.profileService.getLocationDetails(this.locationDetails.id).subscribe({
            next: response => {
              if (response.status && response.data) {
                // Update original data after successful save to match current state
                const updatedLocationDetails = response.data;
                this.originalFormData = {
                  email: updatedLocationDetails.email,
                  contact: updatedLocationDetails.contact,
                };

                // Update form with fresh data from API
                this.contactForm.patchValue(
                  {
                    email: updatedLocationDetails.email,
                    contact: updatedLocationDetails.contact,
                  },
                  { emitEvent: false }
                );

                // Emit updated location details to parent component
                this.locationUpdated.emit(updatedLocationDetails);

                // Reset all modification tracking
                this.isEditing = false;
                this.isFormModified = false;
                this.isSubmitting = false;

                this.cancelClicked.emit();
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: ' Contact updated successfully',
                });
              } else {
                // If refetch fails, still update local state
                this.originalFormData = { ...this.contactForm.value };
                this.isEditing = false;
                this.isFormModified = false;
                this.isSubmitting = false;

                this.cancelClicked.emit();
                this.messageService.add({
                  severity: 'success',
                  summary: 'Success',
                  detail: ' Contact updated successfully',
                });
              }
            },
            error: refetchError => {
              console.error('Error refetching location details', refetchError);
              // Still update local state even if refetch fails
              this.originalFormData = { ...this.contactForm.value };
              this.isEditing = false;
              this.isFormModified = false;
              this.isSubmitting = false;

              this.cancelClicked.emit();
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: ' Contact updated successfully',
              });
            },
          });
        },
        error: error => {
          this.isSubmitting = false;
          console.error('Error updating location details', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update contact details. Please try again.',
          });
        },
      });
    } else {
      this.contactForm.markAllAsTouched();
    }
  }

  onEdit() {
    this.isEditing = true;
    // Store current form values as backup before enabling editing
    this.originalFormData = { ...this.contactForm.value };
    this.isFormModified = false; // Reset modification tracking
    this.contactForm.enable();
  }

  onCancel() {
    this.isEditing = false;
    this.isFormModified = false;
    // Restore original form data
    this.contactForm.patchValue(this.originalFormData);

    this.cancelClicked.emit();
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

  private checkIfFormModified() {
    const currentFormData = this.contactForm.value;

    // Check if form data has changed
    const formDataChanged =
      JSON.stringify(currentFormData) !== JSON.stringify(this.originalFormData);

    this.isFormModified = formDataChanged;
  }
}
